import { useLayoutEffect, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { BrandVersion, isBuyerPortalFeatureEnabled, portalUserService, useBuyerPortalSettings } from 'common';
import { useCompanyPublicData } from '../../services';

const brandCacheKey = (companyToken: string) => `signupBrand:${companyToken}`;

function readCachedBrand(companyToken: string): BrandVersion | null {
    const cached = sessionStorage.getItem(brandCacheKey(companyToken));
    return cached === 'newBrand' || cached === 'transcepta' ? cached : null;
}

function writeCachedBrand(companyToken: string, brand: BrandVersion) {
    sessionStorage.setItem(brandCacheKey(companyToken), brand);
}

function companyTokenFromPath(pathname: string): string | undefined {
    return matchPath<{ companyToken: string }>(pathname, {
        path: '/supplier/:companyToken',
    })?.params.companyToken;
}

function applyBrand(brand: BrandVersion) {
    portalUserService.setBrandVersion(brand);
}

/**
 * Applies signup-portal HighRadius brand from FeaturesEnabled
 * (`EnableSignupPortalHighRadiusBrand`). Brand is applied in useLayoutEffect
 * and the UI stays on a spinner until then — no classic→HighRadius flash.
 */
export function useSignupBuyerBrandSync(): { isBrandReady: boolean } {
    const { pathname } = useLocation();
    const companyToken = companyTokenFromPath(pathname);

    const [isBrandReady, setIsBrandReady] = useState(() => {
        const token = companyTokenFromPath(window.location.pathname);
        if (!token) {
            return true;
        }
        const cached = readCachedBrand(token);
        if (cached) {
            applyBrand(cached);
            return true;
        }
        return false;
    });

    // Token changed (or first mount without cache): reset / apply cache before paint.
    useLayoutEffect(() => {
        if (!companyToken) {
            setIsBrandReady(true);
            return;
        }
        const cached = readCachedBrand(companyToken);
        if (cached) {
            applyBrand(cached);
            setIsBrandReady(true);
            return;
        }
        setIsBrandReady(false);
    }, [companyToken]);

    const { data: company, error: companyError } = useCompanyPublicData({ search: companyToken });
    const { data: buyerPortalSettings, isError: isSettingsError } = useBuyerPortalSettings({
        companyId: company?.ID ?? null,
    });

    // Apply CSP brand before paint, then allow Layout to render.
    useLayoutEffect(() => {
        if (!companyToken || buyerPortalSettings == null) {
            return;
        }

        const brand: BrandVersion = isBuyerPortalFeatureEnabled(
            buyerPortalSettings,
            'EnableSignupPortalHighRadiusBrand'
        )
            ? 'newBrand'
            : 'transcepta';
        applyBrand(brand);
        writeCachedBrand(companyToken, brand);
        setIsBrandReady(true);
    }, [companyToken, buyerPortalSettings]);

    // Fail open to classic if lookup fails so we never spin forever.
    useLayoutEffect(() => {
        if (!companyToken || (!companyError && !isSettingsError)) {
            return;
        }
        applyBrand('transcepta');
        setIsBrandReady(true);
    }, [companyToken, companyError, isSettingsError]);

    return { isBrandReady };
}
